import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, ChevronDown, LogOut, Settings, User, Check } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_TITLES = {
  "/app/dashboard": "Dashboard",
  "/app/projects":  "Projects",
  "/app/payments":  "Payments",
  "/app/reports":   "Reports",
  "/app/team":      "Team",
  "/app/settings":  "Settings",
  "/app/billing":   "Billing",
  "/app/notifications": "Notifications",
  "/app/solar-design":  "Solar Designer",
  "/app/bill-audit":    "Bill Audit",
};

export default function Header({ onMobileMenuToggle }) {
  const { user, workspace, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentNotifs, setRecentNotifs] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef(null);

  const pageTitle = Object.entries(PAGE_TITLES).find(([path]) =>
    location.pathname.startsWith(path)
  )?.[1] || "SunBiz";

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(data.count);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    const close = (e) => { if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const openBell = async () => {
    setBellOpen(p => !p);
    if (!bellOpen) {
      try {
        const { data } = await axios.get(`${API}/notifications`);
        setRecentNotifs(data.slice(0, 8));
      } catch (err) { console.error(err); }
    }
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      setRecentNotifs(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      fetchCount();
    } catch (err) { console.error(err); }
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <header
      style={{
        height: "64px",
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(46,100,68,0.09)",
        flexShrink: 0,
        zIndex: 20,
        position: "sticky",
        top: 0,
      }}
      className="flex items-center justify-between px-5"
    >
      {/* Left: Mobile menu + page title */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMobileMenuToggle}
          className="lg:hidden"
          style={{ color: "#5A7264", background: "none", border: "none", cursor: "pointer", padding: "4px" }}
          data-testid="mobile-menu-btn"
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path d="M3 12h18M3 6h18M3 18h18" strokeLinecap="round" />
          </svg>
        </button>

        <div>
          <h2
            className="font-heading font-semibold text-sm"
            style={{ color: "#1B2B22", letterSpacing: "-0.02em" }}
          >
            {pageTitle}
          </h2>
        </div>
      </div>

      {/* Right: Notifications + User menu */}
      <div className="flex items-center gap-2">
        {/* Notification Bell with Dropdown */}
        <div ref={bellRef} style={{ position: "relative" }}>
          <button
            onClick={openBell}
            data-testid="notifications-btn"
            style={{
              position: "relative",
              width: "36px",
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "9px",
              color: bellOpen ? "#2F7D52" : "#5A7264",
              background: bellOpen ? "#E6F4EC" : "#F0F7F2",
              border: "1px solid rgba(46,100,68,0.1)",
              transition: "background 0.15s ease, color 0.15s ease",
              cursor: "pointer",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#E6F4EC"; e.currentTarget.style.color = "#2F7D52"; }}
            onMouseLeave={e => { if (!bellOpen) { e.currentTarget.style.background = "#F0F7F2"; e.currentTarget.style.color = "#5A7264"; } }}
          >
            <Bell size={16} />
            {unreadCount > 0 && (
              <span style={{ position: "absolute", top: "5px", right: "5px", minWidth: "14px", height: "14px", background: "#DC2626", borderRadius: "7px", border: "1.5px solid white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "8px", fontWeight: "800", color: "#fff", padding: "0 3px" }}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          {bellOpen && (
            <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: "340px", background: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px", boxShadow: "0 12px 40px rgba(0,0,0,0.12)", zIndex: 100, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "13px", fontWeight: "800", color: "#1B2B22" }}>Notifications</span>
                {unreadCount > 0 && <span style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "10px", background: "#FEE2E2", color: "#DC2626", fontWeight: "700" }}>{unreadCount} unread</span>}
              </div>
              <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                {recentNotifs.length === 0 ? (
                  <div style={{ padding: "24px", textAlign: "center", fontSize: "12px", color: "#94A3B8" }}>No notifications</div>
                ) : (
                  recentNotifs.map(n => (
                    <div key={n.id}
                      style={{ display: "flex", gap: "10px", padding: "10px 16px", borderBottom: "1px solid #F8FAFC", background: n.is_read ? "#fff" : "#F0FDF4", cursor: "pointer", transition: "background 0.1s" }}
                      onClick={() => { if (!n.is_read) markRead(n.id); if (n.link) { navigate(n.link); setBellOpen(false); } }}
                      onMouseEnter={e => { e.currentTarget.style.background = n.is_read ? "#F8FAFC" : "#ECFDF5"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? "#fff" : "#F0FDF4"; }}
                    >
                      <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: n.is_read ? "transparent" : "#2F7D52", marginTop: "6px", flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "12px", fontWeight: n.is_read ? "500" : "700", color: "#1B2B22", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.title}</div>
                        <div style={{ fontSize: "11px", color: "#64748B", marginTop: "1px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.message}</div>
                        <div style={{ fontSize: "9px", color: "#94A3B8", marginTop: "2px" }}>{new Date(n.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <Link to="/app/notifications" onClick={() => setBellOpen(false)}
                style={{ display: "block", padding: "10px", textAlign: "center", fontSize: "11px", fontWeight: "700", color: "#2F7D52", borderTop: "1px solid #F1F5F9", textDecoration: "none" }}>
                View All Notifications
              </Link>
            </div>
          )}
        </div>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="user-menu-trigger"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "4px 8px 4px 4px",
              borderRadius: "9px",
              border: "1px solid rgba(46,100,68,0.1)",
              background: "#F0F7F2",
              cursor: "pointer",
              outline: "none",
              transition: "background 0.15s ease",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#E6F4EC"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#F0F7F2"; }}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0"
              style={{ background: "#E6F4EC", color: "#2F7D52", border: "1.5px solid rgba(46,100,68,0.25)" }}
            >
              {user?.full_name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="hidden sm:block text-left">
              <div className="text-xs font-semibold leading-none" style={{ color: "#1B2B22" }}>{user?.full_name}</div>
              <div className="text-[10px] mt-0.5 capitalize" style={{ color: "#8AA496" }}>{user?.role?.replace(/_/g, " ")}</div>
            </div>
            <ChevronDown size={11} style={{ color: "#8AA496" }} />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            style={{ width: "180px", background: "#FFFFFF", border: "1px solid rgba(46,100,68,0.1)", boxShadow: "0 4px 20px rgba(46,100,68,0.12)" }}
          >
            <DropdownMenuItem asChild>
              <Link
                to="/app/settings"
                className="flex items-center gap-2 cursor-pointer"
                data-testid="profile-link"
                style={{ color: "#1B2B22" }}
              >
                <User size={13} />
                <span className="text-sm">Profile & Settings</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator style={{ background: "rgba(46,100,68,0.08)" }} />
            <DropdownMenuItem
              onClick={handleLogout}
              data-testid="header-logout-btn"
              className="cursor-pointer"
              style={{ color: "#DC2626" }}
            >
              <LogOut size={13} className="mr-2" />
              <span className="text-sm">Logout</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
